import { derivePromotedBlockId } from "./invitationIdentity";

const toNormalizedId = (value) => String(value ?? "").trim().toLowerCase();

const dedupeNormalizedIds = (values = []) => {
  if (!Array.isArray(values) || values.length === 0) return [];

  const seen = new Set();
  const deduped = [];

  values.forEach((value) => {
    const normalized = toNormalizedId(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    deduped.push(normalized);
  });

  return deduped;
};

export const derivePromotedIdMap = ({ pendingUserId, blockIds } = {}) => {
  const normalizedPendingUserId = String(pendingUserId ?? "").trim();
  if (!normalizedPendingUserId) return {};

  return dedupeNormalizedIds(blockIds).reduce((accumulator, blockId) => {
    const promotedBlockId = derivePromotedBlockId({
      pendingUserId: normalizedPendingUserId,
      pendingBlockId: blockId,
    });
    const normalizedPromotedBlockId = toNormalizedId(promotedBlockId);
    if (!normalizedPromotedBlockId) return accumulator;

    accumulator[blockId] = normalizedPromotedBlockId;
    return accumulator;
  }, {});
};

const hasPendingBlockQueueEntries = (client) => {
  if (!client || typeof client !== "object") return false;

  const queueFields = ["clientTransactionData", "clientDeletionData"];
  return queueFields.some(
    (fieldName) =>
      Array.isArray(client[fieldName]) && client[fieldName].length > 0
  );
};

const remapQueueIds = (ids, promotedIdMap) =>
  dedupeNormalizedIds(ids).map((id) => promotedIdMap[id] ?? id);

export const remapClientPendingBlockIdsToPromotedIds = ({
  client,
  pendingUserId,
} = {}) => {
  if (!client || typeof client !== "object") {
    return {
      changed: false,
      promotedIdMap: {},
    };
  }

  const sourceBlockIds = [];
  if (Array.isArray(client.blocks)) {
    client.blocks.forEach((block) => {
      const blockId = toNormalizedId(block?.id);
      if (blockId) {
        sourceBlockIds.push(blockId);
      }
    });
  }

  if (Array.isArray(client.clientTransactionData)) {
    client.clientTransactionData.forEach((id) => sourceBlockIds.push(id));
  }
  if (Array.isArray(client.clientDeletionData)) {
    client.clientDeletionData.forEach((id) => sourceBlockIds.push(id));
  }

  const promotedIdMap = derivePromotedIdMap({
    pendingUserId,
    blockIds: sourceBlockIds,
  });
  if (Object.keys(promotedIdMap).length === 0) {
    return {
      changed: false,
      promotedIdMap,
    };
  }

  let changed = false;

  if (Array.isArray(client.blocks)) {
    client.blocks.forEach((block) => {
      const currentBlockId = toNormalizedId(block?.id);
      const nextBlockId = promotedIdMap[currentBlockId];
      if (!currentBlockId || !nextBlockId || currentBlockId === nextBlockId) {
        return;
      }

      changed = true;
      if (typeof block?.setProperty === "function") {
        block.setProperty("id", nextBlockId);
      } else if (block && typeof block === "object") {
        block.id = nextBlockId;
      }
    });
  }

  if (Array.isArray(client.clientTransactionData)) {
    const nextTransactionIds = remapQueueIds(
      client.clientTransactionData,
      promotedIdMap
    );
    if (
      nextTransactionIds.length !== client.clientTransactionData.length ||
      nextTransactionIds.some(
        (id, index) => id !== toNormalizedId(client.clientTransactionData[index])
      )
    ) {
      changed = true;
    }
    client.clientTransactionData = nextTransactionIds;
  }

  if (Array.isArray(client.clientDeletionData)) {
    const nextDeletionIds = remapQueueIds(client.clientDeletionData, promotedIdMap);
    if (
      nextDeletionIds.length !== client.clientDeletionData.length ||
      nextDeletionIds.some(
        (id, index) => id !== toNormalizedId(client.clientDeletionData[index])
      )
    ) {
      changed = true;
    }
    client.clientDeletionData = nextDeletionIds;
  }

  return {
    changed,
    promotedIdMap,
  };
};

export const shouldRetainAcceptedPendingClientForDeferredSync = (client) => {
  if (!client || typeof client !== "object") return false;
  if (client?.status === "active-string") return false;
  if (client?.usePendingBlocksPath !== true) return false;

  const hasQueuedWrites =
    typeof client?.hasPendingQueuedWrites === "function"
      ? client.hasPendingQueuedWrites()
      : hasPendingBlockQueueEntries(client);

  return hasQueuedWrites || client?.syncStatusManager?.hasChangesOffline === true;
};
